import { Injectable } from '@angular/core';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import sum from 'lodash-es/sum';
import { Subject } from 'rxjs';

export interface IStickyEntry {
    order: number;
    uid: string;
    hidden?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class StickyFreeService {
    private entries: IStickyEntry[] = [];
    private stickyRange?: string;

    readonly entriesChange = new Subject<void>();

    constructor(private elementHeightService: ElementHeightService) {}

    add(entry: IStickyEntry) {
        this.entries.push(entry);
        this.update();
        return () => {
            const index = this.entries.indexOf(entry);
            if (index > -1) {
                this.entries.splice(index, 1);
                this.update();
            }
        };
    }

    shift(uid: string, hidden: boolean) {
        const entry = this.entries.find(i => i.uid === uid);
        if (entry && !entry.hidden !== !hidden) {
            entry.hidden = hidden;
            const hiddenItems = this.entries.filter(i => i.hidden);
            document.documentElement.style.setProperty(
                '--sticky-shift',
                hiddenItems.length ? `calc(${['0px', ...hiddenItems.map(i => this.toVar(i.uid))].join(' - ')})` : '0px'
            );
        }
    }

    toVar(uid: string): string {
        return `var(--element-height-${uid}, 0px)`;
    }

    update() {
        this.entriesChange.next();
    }

    getOffsets(order: number): string {
        const offsetEntries = this.entries.filter(i => i.order < order).map(i => this.toVar(i.uid));
        if (offsetEntries.length) {
            // the last piece here is 1px up shift to fix problems with browser rounding calc (what caused 1px gap between stickies sometimesqgit)
            return `calc( ${offsetEntries.join('+')} + var(--sticky-shift, 0px) - ${offsetEntries.length}px )`;
        }
        return 'var(--sticky-shift, 0px)';
    }

    getStickyHeight(skipHidden = false) {
        return sum(this.entries.filter(i => !i.hidden || !skipHidden).map(i => this.elementHeightService.getState(i.uid).value || 0));
    }

    setStickyRange(range?: string) {
        this.stickyRange = range;
        if (range) {
            document.documentElement.style.setProperty('--sticky-range', range);
        } else {
            document.documentElement.style.removeProperty('--sticky-range');
        }
    }
}
